<template>
  <v-menu
    translate="slide-y"
    elevation="4"
    offset-y
    left
    nudge-bottom="16px"
    min-width="220px"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        v-on="on"
        v-bind="attrs"
        rounded
        large
        outlined
        class="button-activator pr-0"
        elevation="0"
        :height="$vuetify.breakpoint.mobile ? 40 : 48"
        color="gray"
        :class="$vuetify.breakpoint.mobile ? 'pl-0 ml-2' : 'pl-2 ml-4'"
        :min-width="$vuetify.breakpoint.mobile ? 0 : 82"
      >
        <v-icon
          v-if="!$vuetify.breakpoint.mobile"
          size="20"
          color="black"
          class=""
        >
          $chevrondown
        </v-icon>
        <user-avatar
          :badge="
            hasUnpaidInvoice
              ? 'error'
              : hasTrialOrSuspendedService
              ? 'warning'
              : ''
          "
          :size="$vuetify.breakpoint.mobile ? 40 : 48"
        ></user-avatar>
      </v-btn>
    </template>
    <v-list class="dropdown-left v-list--small">
      <v-list-item class="primary--text" v-if="allowToChangeMode">
        <v-list-item-title class="p-2">
          <div class="d-flex align-center">
            <v-checkbox
              class="v-input--switch mt-0 pt-0 mr-3 primary--text"
              :input-value="selectedStyle.darkMode"
              ref="darkModeSwitcher"
            >
              <template v-slot:append>
                <div class="v-input__control">
                  <div class="v-input__slot">
                    <div
                      class="v-input--selection-controls__input"
                      @click.stop="
                        changeDarkModeSelection(!selectedStyle.darkMode)
                      "
                    >
                      <input role="switch" />
                      <div class="v-input--switch__track"></div>
                      <div class="v-input--switch__thumb">
                        <v-icon
                          v-show="!selectedStyle.darkMode"
                          size="13"
                          color="gray darken-1"
                          >$sun</v-icon
                        >
                        <v-icon
                          v-show="selectedStyle.darkMode"
                          size="13"
                          color="primary"
                          >$moon</v-icon
                        >
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </v-checkbox>
            <p v-if="selectedStyle.darkMode" class="p-2 mb-0 text--darken2">
              {{ $t("heading.styleManager.darkMode.darkMode") }}
            </p>
            <p v-else class="p-2 mb-0 text--darken2">
              {{ $t("heading.styleManager.darkMode.lightMode") }}
            </p>
          </div>
        </v-list-item-title>
      </v-list-item>
      <v-divider v-if="allowToChangeMode"></v-divider>
      <v-list-item class="primary--text" to="/my-account">
        <v-list-item-title class="p-2"
          ><v-icon size="20" class="mr-4 gray--icon">$user</v-icon>
          {{ $t("button.account.details") }}
        </v-list-item-title>
      </v-list-item>
      <v-list-item v-if="billingEnabled" class="primary--text" to="/services">
        <v-list-item-title class="p-2">
          <v-icon size="20" class="mr-4 gray--icon">$plans</v-icon>
          <v-badge
            class="user-dropdown-dot-badge"
            :value="hasTrialOrSuspendedService"
            color="warning"
            dot
          />
          {{ $t("general.myPlans") }}
        </v-list-item-title>
      </v-list-item>
      <v-list-item v-if="billingEnabled" class="primary--text" to="/billing">
        <v-list-item-title class="p-2">
          <v-icon size="20" class="mr-4 gray--icon">$dollarsign</v-icon>
          <v-badge
            class="user-dropdown-dot-badge"
            :value="hasUnpaidInvoice"
            color="error"
            dot
          />
          {{ $t("general.myBilling") }}
        </v-list-item-title>
      </v-list-item>
      <v-list-item v-if="isAppSingleServer" class="primary--text" target="_blank" :href="discordUrl">
        <v-list-item-title class="p-2">
          <v-icon size="22" class="mr-4 gray--icon">$discordIcon</v-icon>
          {{ $t("general.joinOurCommunity") }}
        </v-list-item-title>
      </v-list-item>
      <v-list-item v-else class="primary--text" target="_blank" :href="supportUrl">
        <v-list-item-title class="p-2">
          <v-icon size="20" class="mr-4 gray--icon">$supportIcon</v-icon>
          {{ $t("general.support") }}
        </v-list-item-title>
      </v-list-item>
      <!--
<v-list-item class="primary--text" to="/nodata">
  <v-list-item-title class="p-2"
    ><v-icon size="20" class="mr-4 gray--icon">$bell</v-icon
    >Notifications</v-list-item-title
  >
</v-list-item>
-->
      <v-divider></v-divider>
      <v-list-item class="primary--text" @click="logout">
        <v-list-item-title class="p-2"
          ><v-icon size="20" class="mr-4 gray--icon">$logout</v-icon>
          {{ $t("button.logout") }}
        </v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script>
import UserAvatar from "../UserAvatar.vue";
import Auth from "@/apis/Auth";
import Api from "@/apis/Api";
import StyleManagerMixin from "../../mixins/StyleManagerMixin";

export default {
  components: {
    UserAvatar,
  },
  mixins: [StyleManagerMixin],
  data: function () {
    return {
      lightLogoData: this.$store.state.ThemeModule.lightLogo,
      darkLogoData: this.$store.state.ThemeModule.darkLogo,
      faviconData: this.$store.state.ThemeModule.favicon,
      selectedColors: this.$store.state.ThemeModule.colors.primary,
      selectedStyle: this.$store.state.ThemeModule.style,
      open: false,
    };
  },
  computed: {
    isAppSingleServer() {
      return !!this.$store.state.home.app.single_server?.active;
    },
    allowToChangeMode() {
      if (this.isAppSingleServer) {
        return true;
      }
      return (
        this.selectedStyle.allowToChangeMode && !this.$store.state.home.admin
      );
    },
    isDark() {
      return this.$vuetify.theme.dark;
    },
    hasTrialOrSuspendedService() {
      return (
        this.billingEnabled &&
        this.$store.state.services.data &&
        this.$store.state.services.hasTrialOrSuspendedService()
      );
    },
    hasUnpaidInvoice() {
      // TODO
      return false;
    },
    discordUrl() {
      if (this.isAppSingleServer) {
        return "https://discord.gg/9twHWR7xGX";
      }
      return "";
    },
    supportUrl() {
      return this.$store.state.home?.app?.support_link;
    },
    billingEnabled() {
      if (this.$store.state.home?.app?.billing_enabled) {
        return true;
      }
      return false;
    },
  },
  watch: {
    isDark(newVal) {
      localStorage.setItem("isDarkMode", newVal ? "true" : "false");
    },
  },
  methods: {
    changeDarkModeSelection: function (value) {
      this.$refs.darkModeSwitcher.$refs.input.click();
      this.$store.state.ThemeModule.style.darkMode = value;
      this.$vuetify.theme.dark = value;

      Api.put("/user/dark-mode", {
        dark_mode: value,
      });

      this.changeColorsTheme();
    },
    logout() {
      Auth.logout()
        .then(() => {
          this.$root.$emit("loggedOut", true);
          this.$router.push({ path: "/login" });
        })
        .catch(() => {});
    },
  },
  mounted: function () {
    this.$nextTick(() => {
      this.selectedStyle = this.$store.state.ThemeModule.style;
      document.body.classList.remove("is-dark", "is-light");
      document.body.classList.add(
        this.selectedStyle.darkMode ? "is-dark" : "is-light"
      );

      setTimeout(() => {
        document.body.classList.remove("no-transition");
      }, 100);
    });

    const savedTheme = localStorage.getItem("theme");

    if (savedTheme) {
      const theme = JSON.parse(savedTheme);
      const isDarkMode = theme.style.darkMode;
      localStorage.setItem("isDarkMode", isDarkMode ? "true" : "false");
    }
  },
};
</script>

<style lang="scss" scoped>
.v-btn::v-deep {
  .v-icon {
    svg {
      transition: color 0.24s ease;
      color: var(--v-darkicon-base) !important;
    }
  }
  &:hover {
    .v-icon {
      svg {
        color: var(--v-primary-base) !important;
      }
    }
  }
}
.user-dropdown-dot-badge::v-deep {
  position: absolute;
  left: 30px;
  top: -2px;
  &.v-badge.v-badge--dot .v-badge__badge {
    width: 6px;
    height: 6px;
    &:after {
      border-width: 1px;
    }
  }
}
.v-input--checkbox::v-deep {
  &.v-input--switch {
    > .v-input__control {
      display: none;
    }
    .v-input__slot {
      margin-bottom: 0;
      min-height: unset;
    }
    .v-input {
      &__append-outer {
        margin-left: 3px;
      }
    }
  }
}
</style>
